import { useEffect, useState } from "react";
import { PenLine } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Ornament } from "@/components/Ornament";
import { useLanguage } from "@/context/LanguageContext";
import { strings } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LivreDor() {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [messages, setMessages] = useState([]);
  const { lang } = useLanguage();
  const s = strings.livreOr[lang];

  useEffect(() => {
    fetch(`${API}/guestbook`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setMessages)
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim() || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/guestbook`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), message: message.trim() }),
      });
      if (!res.ok) throw new Error();
      setStatus("sent");
      setName("");
      setMessage("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="livre-or-page">
      <section className="mx-auto max-w-3xl px-6 pt-16 md:px-8 md:pt-24">
        <FadeIn>
          <div className="text-center">
            <p className="eyebrow" data-testid="livre-or-eyebrow">
              {s.eyebrow}
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium tracking-[0.12em] text-noir sm:text-5xl" data-testid="livre-or-title">
              {s.title}
            </h1>
            <div className="mt-10">
              <Ornament />
            </div>
            <div className="mx-auto mt-10 max-w-xl space-y-4 font-sans text-sm font-light leading-loose text-noir/75 [text-wrap:pretty] md:text-base" data-testid="livre-or-intro">
              <p>
                {s.p1}
              </p>
              <p>
                {s.p2}
              </p>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="double-frame mt-14" data-testid="livre-or-form-frame">
            <div className="double-frame-inner px-6 py-10 md:px-12 md:py-14">
              {status === "sent" ? (
                <div className="text-center" data-testid="livre-or-confirmation">
                  <p className="font-serif text-2xl italic text-bordeaux md:text-3xl">
                    {s.confirmTitle}
                  </p>
                  <p className="mx-auto mt-5 max-w-md font-sans text-sm font-light leading-loose text-noir/70">
                    {s.confirmText}
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="btn-outline mt-10"
                    data-testid="livre-or-new-message-button"
                  >
                    {s.another}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="livre-or-form">
                  <label htmlFor="livre-or-name" className="eyebrow block">
                    {s.nameLabel}
                  </label>
                  <input
                    id="livre-or-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    required
                    data-testid="livre-or-name-input"
                    className="mt-3 w-full border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light text-noir outline-none transition-colors duration-300 placeholder:text-noir/30 focus:border-bordeaux"
                    placeholder={s.namePlaceholder}
                  />
                  <label htmlFor="livre-or-message" className="eyebrow mt-8 block">
                    {s.messageLabel}
                  </label>
                  <textarea
                    id="livre-or-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={1000}
                    required
                    rows={5}
                    data-testid="livre-or-message-input"
                    className="mt-3 w-full resize-none border border-bordeaux/30 bg-transparent px-4 py-3.5 font-sans text-sm font-light leading-relaxed text-noir outline-none transition-colors duration-300 placeholder:text-noir/30 focus:border-bordeaux"
                    placeholder={s.messagePlaceholder}
                  />
                  {status === "error" && (
                    <p className="mt-4 text-center font-sans text-xs text-bordeaux" data-testid="livre-or-error">
                      {s.error}
                    </p>
                  )}
                  <div className="mt-10 text-center">
                    <button type="submit" className="btn-primary" disabled={status === "sending"} data-testid="livre-or-submit-button">
                      <PenLine size={14} strokeWidth={1.5} />
                      {status === "sending" ? s.sending : s.submit}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-20 md:px-8 md:py-28" data-testid="livre-or-messages-section">
        <FadeIn>
          <div className="text-center">
            <Ornament />
            <h2 className="mt-8 font-serif text-3xl font-medium text-noir md:text-4xl" data-testid="livre-or-messages-title">
              {s.messagesTitle}
            </h2>
          </div>
        </FadeIn>
        {messages.length === 0 ? (
          <FadeIn delay={0.1}>
            <p className="mt-10 text-center font-serif text-lg italic text-noir/50" data-testid="livre-or-empty-note">
              {s.empty}
            </p>
          </FadeIn>
        ) : (
          <div className="mt-12 space-y-12" data-testid="livre-or-messages-list">
            {messages.map((m) => (
              <FadeIn key={m.id}>
                <figure className="text-center" data-testid={`livre-or-message-${m.id}`}>
                  <blockquote className="font-serif text-xl italic leading-relaxed text-noir/85 md:text-2xl">
                    « {m.message} »
                  </blockquote>
                  <figcaption className="mt-4 font-sans text-[11px] uppercase tracking-[0.3em] text-bordeaux">
                    {m.name}
                  </figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
